import { useCallback, useContext } from "react";
import "./DsLastSynced.scss";
import useIntegratedDataSources from "../Integrations/DataSources/useIntegratedDataSources";
import { useTranslation } from "react-i18next";
import { AppPreferencesContext } from "../../General/AppPreferencesProvider";
import { useHistory } from "react-router-dom";
import DsLoadingState from "./DsLoadingState";
import LastSyncItem from "./LastSyncItem";
import { STATUSES_TO_FILTER } from "../Integrations/DataSources/DataSourcesUtils";
import { ConnectionStatusEnum } from "@SeemplicityDev/shared/integrations";
import NoDataSources from "../../General/NoDataSources/NoDataSources";
import {
  getFriendlyTimeFromNow,
  parseToDateObj,
  Loader,
  useIcon,
} from "@SeemplicityDev/shared";

/**
 * Node shape consumed by DsLastSynced. The GraphQL response type
 * (`IntegratedDataSourcesResponse`) types datasource nodes loosely with an
 * index signature, so we narrow to the concrete fields this component reads.
 */
interface DsLastSyncedDatasourceNode {
  id?: string;
  last_collection_time?: string | null;
  enabled?: boolean;
  check_connection_status?: string;
  datasource_definition?: {
    id?: string;
    name?: string;
    status?: string;
  };
}

interface DsLastSyncedEdge {
  node?: DsLastSyncedDatasourceNode;
}

interface DsLastSyncedProps {
  close: () => void;
}

const DsLastSynced: React.FC<DsLastSyncedProps> = ({ close }) => {
  const { t } = useTranslation();
  const { integratedDataSources, integratedDataSourcesError } =
    useIntegratedDataSources(true);
  const { getDataSourceIcon } = useIcon();

  const {
    appPreferences: { hasDatasources },
  } = useContext(AppPreferencesContext);
  const { push } = useHistory();

  const onDsClicked = (datasourceId?: string) => {
    push(`/settings/integrations/dataSources/${datasourceId}`);
    close();
  };

  const getLastSyncItem = (datasource?: DsLastSyncedDatasourceNode) => {
    if (!datasource) {
      return null;
    }

    const lastUpdate = datasource?.last_collection_time
      ? getFriendlyTimeFromNow(datasource?.last_collection_time)
      : undefined;
    const title = datasource?.datasource_definition?.name;
    // size "S" makes getDataSourceIcon return a string|undefined; the declared
    // union also includes DataSourceIcon, so narrow back to the string source.
    const src = getDataSourceIcon(
      datasource?.datasource_definition?.name ?? "",
      "S"
    ) as string | undefined;
    const name = datasource?.datasource_definition?.name;

    return (
      <LastSyncItem
        lastUpdate={lastUpdate}
        title={title}
        src={src}
        name={name}
        key={datasource?.id}
        id={datasource?.datasource_definition?.id}
        onClickHandler={onDsClicked}
        connectionStatus={datasource.check_connection_status}
      />
    );
  };

  const sortArrayByLastReceivedAndAlphabeit = (array: DsLastSyncedEdge[]) => {
    return array.sort((a, b) => {
      if (a?.node?.last_collection_time === null) {
        if (b?.node?.last_collection_time === null) {
          // Reaching this branch guarantees both nodes exist (we read
          // `node.last_collection_time` above), so assert through to `name`.
          return a
            .node!.datasource_definition!.name!.trim()
            .localeCompare(b.node!.datasource_definition!.name!.trim());
        }

        return 1;
      } else if (b?.node?.last_collection_time === null) {
        return -1;
      }

      return (
        (parseToDateObj(b!.node!.last_collection_time!) as unknown as number) -
        (parseToDateObj(a!.node!.last_collection_time!) as unknown as number)
      );
    });
  };

  const getContentByState = useCallback(() => {
    // The GraphQL response types nodes with an index signature; narrow the
    // edges to the concrete fields this component reads.
    const edges = integratedDataSources?.datasources?.edges as
      | DsLastSyncedEdge[]
      | undefined;
    if (edges?.length) {
      const filteredArray = edges?.filter((el) => {
        return (
          !STATUSES_TO_FILTER.includes(
            el?.node?.datasource_definition?.status ?? ""
          ) &&
          el?.node?.datasource_definition?.name !== "Manual" &&
          el?.node?.enabled &&
          (!!el?.node?.last_collection_time ||
            el?.node?.check_connection_status === ConnectionStatusEnum.FAILED)
        );
      });

      const aggregatedByNameArray = filteredArray.reduce<DsLastSyncedEdge[]>(
        (result, currDatasource) => {
          const index = result.findIndex(
            (el) =>
              el?.node?.datasource_definition?.name ===
              currDatasource?.node?.datasource_definition?.name
          );

          if (index === -1) {
            result?.push(currDatasource);
          } else {
            if (!!currDatasource?.node?.last_collection_time) {
              if (
                !result[index]?.node?.last_collection_time ||
                parseToDateObj(currDatasource!.node!.last_collection_time!)! >=
                  parseToDateObj(result[index]!.node!.last_collection_time!)!
              ) {
                result[index] = currDatasource;
              }
            }

            if (
              currDatasource?.node!.check_connection_status ===
              ConnectionStatusEnum.FAILED
            ) {
              result[index].node!.check_connection_status =
                ConnectionStatusEnum.FAILED;
            }
          }

          return result;
        },
        []
      );
      return (
        <ul className={"last-sync-list"}>
          {sortArrayByLastReceivedAndAlphabeit(aggregatedByNameArray).map(
            (el) => getLastSyncItem(el.node)
          )}
        </ul>
      );
    }
    return <DsLoadingState />;
  }, [integratedDataSources]);

  if (!hasDatasources) {
    return (
      <div className="actions-menu last-sync-wrapper is-empty">
        <NoDataSources
          isFullPage={false}
          actionSubtitle={t("Set up your first Datasource Integration")}
          callback={close}
        />
      </div>
    );
  }

  const emptyDs = integratedDataSources?.datasources?.edges?.length === 0;

  return (
    <div className={`actions-menu last-sync-wrapper`}>
      {emptyDs ? (
        <Loader
          center
          className={"ds-last-synced-loader-error"}
          icon={"seem-icon-error"}
          iconSize={"xxl"}
          subtitle={"No data sources found"}
        />
      ) : integratedDataSourcesError ? (
        <Loader
          center
          className={"ds-last-synced-loader-error"}
          icon={"seem-icon-error"}
          iconSize={"xxl"}
          subtitle={"Error connecting to Data Sources"}
        />
      ) : (
        <div className={`data-table`}>
          <div className="data-table-row">
            <div
              className={`data-table-cell data-table-cell-header integration-header`}
            >
              {t("Integration")}
            </div>
            <div
              className={`data-table-cell data-table-cell-header update-content-header`}
            >
              {t("Last Collected")}
            </div>
          </div>
          <div className={"data-table-content"}>{getContentByState()}</div>
        </div>
      )}
    </div>
  );
};
export default DsLastSynced;
